/**
 * HR-dossier (FO-086) — DTO's voor het samenbrengende dossieroverzicht per
 * medewerker.
 *
 * Het dossier is een READ-ONLY aggregatie over bestaande bronnen (gesprekken/
 * verslagen, documenten, beoordelingen, verzuim, opleidingen, certificaten,
 * dossiernotities). Geen nieuw opslagmodel. Elke sectie erft het recht van zijn
 * bron-module; de verzuim-sectie wordt geredigeerd zonder `VERZUIM.canView`
 * (alleen bestaan/status/periode, nooit klachten/notities/medische inhoud).
 *
 * De DTO's bevatten BEWUST geen vrije-tekst-inhoud (klachten, notitie-inhoud,
 * beoordeling-commentaar, gespreksverslag-inhoud): het overzicht toont compacte,
 * geredigeerde rijen met een doorklik naar de bron-tab. Zo lekt het dossier
 * nooit gevoelige inhoud, ook niet in de gecombineerde tijdlijn.
 */

export const DOSSIER_BRON = [
  'GESPREK',
  'DOCUMENT',
  'BEOORDELING',
  'VERZUIM',
  'OPLEIDING',
  'CERTIFICAAT',
  'NOTITIE',
  // batch-67 hr-brieven (FO-506) — de keten van besluiten en brieven.
  'HR_BESLUIT',
] as const
export type DossierBron = (typeof DOSSIER_BRON)[number]

export const DOSSIER_BRON_LABELS: Record<DossierBron, string> = {
  GESPREK:     'Gesprekken & verslagen',
  DOCUMENT:    'Documenten',
  BEOORDELING: 'Beoordelingen',
  VERZUIM:     'Verzuim',
  OPLEIDING:   'Opleidingen',
  CERTIFICAAT: 'Certificaten',
  NOTITIE:     'Dossiernotities',
  HR_BESLUIT:  'Besluiten en brieven',
}

export type DossierBadgeVariant =
  | 'info'
  | 'success'
  | 'warning'
  | 'danger'
  | 'secondary'

/**
 * Eén compacte, redactie-veilige rij binnen een sectie. `titel`/`subtitel` zijn
 * afgeleide labels (nooit vrije tekst/medische inhoud).
 */
export interface DossierSectieItem {
  id: string
  /** ISO (date of datetime); UI formatteert in Europe/Amsterdam. Null = onbekend. */
  datum: string | null
  titel: string
  subtitel: string | null
  status: string | null
  badge: DossierBadgeVariant | null
  /** Doorklik naar de bron-tab/-detail (relatieve route). Null = geen link. */
  linkRef: string | null
}

export interface DossierSectie {
  bron: DossierBron
  titel: string
  /**
   * Heeft de kijker toegang tot deze sectie? `false` = sectie wordt helemaal
   * niet getoond (recht ontbreekt). De verzuim-sectie blijft `aanwezig: true`
   * maar met `geredigeerd: true` als alleen het bestaan getoond mag worden.
   */
  aanwezig: boolean
  /** Alleen voor de verzuim-sectie relevant: inhoud geredigeerd (geen medische data). */
  geredigeerd: boolean
  /** Totaal aantal niet-verwijderde items in de bron (voor "X items"). */
  totaal: number
  /** Link naar de volledige bron-tab ("alles tonen"). Null = geen aparte tab. */
  allesTonenHref: string | null
  /** Meest relevante items (recent), compact en redactie-veilig. */
  items: DossierSectieItem[]
}

/** Eén genormaliseerd item in de gecombineerde chronologische dossier-tijdlijn. */
export interface DossierTijdlijnItem {
  id: string
  bron: DossierBron
  /** Korte type-aanduiding (bv. "Verslag vrijgegeven", "Certificaat behaald"). */
  type: string
  /** ISO-instant (UTC opgeslagen); UI sorteert/formatteert in Europe/Amsterdam. */
  datum: string
  titel: string
  status: string | null
  badge: DossierBadgeVariant | null
  linkRef: string | null
}

export const DOSSIER_SIGNAAL_TYPE = [
  'OPEN_VERSLAG',
  'ONGETEKEND_VERSLAG',
  'CERTIFICAAT_VERLOOPT',
  'CERTIFICAAT_VERLOPEN',
  'LANGDURIG_VERZUIM',
] as const
export type DossierSignaalType = (typeof DOSSIER_SIGNAAL_TYPE)[number]

/** Een compleetheids-/aandachtssignaal bovenaan het dossier. */
export interface DossierSignaal {
  type: DossierSignaalType
  ernst: 'info' | 'warning' | 'danger'
  tekst: string
  linkRef: string | null
}

/** Volledige dossier-overzicht-respons (`GET /employees/:id/dossier`). */
export interface DossierOverzichtDTO {
  employeeId: string
  employeeNaam: string
  secties: DossierSectie[]
  tijdlijn: DossierTijdlijnItem[]
  signalen: DossierSignaal[]
  /** True als de kijker de verzuim-inhoud niet mag zien (alleen het bestaan). */
  verzuimGeredigeerd: boolean
  /** Welke secties de kijker mag zien (server-side bepaald). */
  toegang: {
    documenten: boolean
    verzuim: boolean
    opleidingen: boolean
    notities: boolean
  }
}
